import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { pageTransition } from "@/lib/utils";
import { getCurrentOrg, listDepartments, createDepartment } from "@/api/organizations";
import { resetOnboarding } from "@/api/signup";
import { useToast } from "@/hooks/useToast";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Tabs } from "@/components/ui/Tabs";
import { Badge } from "@/components/ui/Badge";
import { Plus } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import type { Organization, DepartmentFull } from "@/types/organization";
import { LeavePolicySettings } from "./LeavePolicySettings";
import { SecuritySettings } from "./SecuritySettings";
import { ProjectsSettings } from "./ProjectsSettings";
import { DocumentTemplatesSettings } from "./DocumentTemplatesSettings";
import { DocumentConfigSettings } from "./DocumentConfigSettings";
import { EmailTemplatesSettings } from "./EmailTemplatesSettings";
import { ComplianceSettings } from "./ComplianceSettings";
import { BillingSettings } from "./BillingSettings";
import { IntegrationsSettings } from "./IntegrationsSettings";
import { AuditLogSettings } from "./AuditLogSettings";
import { ExportsSettings } from "./ExportsSettings";
import { UsersSettings } from "./UsersSettings";
import { OrgSecurityPolicySettings } from "./OrgSecurityPolicySettings";
import { DataAndDeletionSettings } from "./DataAndDeletionSettings";
import { PrivacyRequestsSettings } from "./PrivacyRequestsSettings";
import { PayrollIntegrationSettings } from "./PayrollIntegrationSettings";
import { CustomFieldsSettings } from "./CustomFieldsSettings";
import { WorkflowsSettings } from "./WorkflowsSettings";
import { SSOSettings } from "./SSOSettings";
import { EmailLogSettings } from "./EmailLogSettings";
import { useAuth } from "@/hooks/useAuth";

export function SettingsPage() {
  const { addToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("general");
  // Audit-log tab is gated by VIEW_AUDIT_LOG (super-admin only). We mirror
  // the backend policy here to avoid even rendering the tab for HR admins.
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [org, setOrg] = useState<Organization | null>(null);
  const [departments, setDepartments] = useState<DepartmentFull[]>([]);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");
  const [newDeptCode, setNewDeptCode] = useState("");

  useEffect(() => {
    Promise.all([getCurrentOrg(), listDepartments()])
      .then(([o, d]) => {
        setOrg(o);
        setDepartments(d);
      })
      .catch(() => addToast("error", "Failed to load settings"));
  }, [addToast]);

  const handleRerunWizard = async () => {
    if (!window.confirm(
      "Re-run the setup wizard? Your existing data will stay intact; this only clears the wizard's progress so you can step through it again."
    )) return;
    try {
      await resetOnboarding();
      navigate("/onboarding");
    } catch {
      addToast("error", "Failed to reset onboarding");
    }
  };

  const handleCreateDept = async () => {
    if (!newDeptName.trim()) return;
    try {
      const dept = await createDepartment({
        name: newDeptName,
        code: newDeptCode || undefined,
      });
      setDepartments((prev) => [...prev, dept]);
      setShowDeptModal(false);
      setNewDeptName("");
      setNewDeptCode("");
      addToast("success", "Department created");
    } catch {
      addToast("error", "Failed to create department");
    }
  };

  const tabs = [
    { id: "general", label: "General" },
    { id: "departments", label: "Departments", count: departments.length },
    { id: "leave", label: "Leave Policies" },
    { id: "time", label: "Time & Projects" },
    { id: "templates", label: "Document Templates" },
    { id: "doc-config", label: "Document Config" },
    { id: "email-templates", label: "Email Templates" },
    { id: "email-log", label: "Email Log" },
    { id: "compliance", label: "Compliance" },
    { id: "billing", label: "Billing" },
    { id: "security", label: "Security" },
    { id: "sso", label: "SSO" },
    { id: "privacy", label: "Privacy" },
    { id: "integrations", label: "Integrations" },
    { id: "custom-fields", label: "Custom Fields" },
    { id: "workflows", label: "Workflows" },
    ...(isSuperAdmin
      ? [
          { id: "audit", label: "Audit Log" },
          // Users sits between Audit Log and Bulk Exports per spec —
          // surfaced only to SUPER_ADMIN because the page can mutate
          // role assignments + deactivate accounts.
          { id: "users", label: "Users" },
          { id: "exports", label: "Bulk Exports" },
          { id: "payroll", label: "Payroll" },
        ]
      : []),
  ];

  return (
    <motion.div {...pageTransition}>
      <PageHeader title="Settings" subtitle="Manage your organization" />
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className="mb-6" />

      {activeTab === "general" && org && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Organization Profile</CardTitle>
            </CardHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Company Name" value={org.name} readOnly />
              <Input label="Slug" value={org.slug} readOnly />
              <Input label="Domain" value={org.domain || ""} readOnly />
              <Input label="EIN" value={org.ein || ""} readOnly />
              <div className="md:col-span-2">
                <Input label="Address" value={org.address || ""} readOnly />
              </div>
            </div>
            <p className="text-xs text-text-muted mt-4">
              Contact your administrator to update organization details.
            </p>
          </Card>

          {isSuperAdmin && (
            <Card>
              <CardHeader>
                <CardTitle>Setup wizard</CardTitle>
              </CardHeader>
              <div className="flex items-start justify-between gap-6">
                <p className="text-sm text-text-muted max-w-xl">
                  Re-run the guided setup wizard to revisit company profile,
                  departments, document templates, leave policy, integrations,
                  and team invites. Existing data is preserved — this only
                  reopens the wizard so you can walk through it again.
                </p>
                <Button variant="secondary" onClick={handleRerunWizard}>
                  Re-run setup wizard
                </Button>
              </div>
            </Card>
          )}

          {/* Data portability + scheduled deletion — SUPER_ADMIN only. The
              server enforces the role too; gating the render here keeps
              the UI clean for HR admins who shouldn't see it at all. */}
          {isSuperAdmin && <DataAndDeletionSettings />}
        </div>
      )}

      {activeTab === "departments" && (
        <Card>
          <CardHeader>
            <CardTitle>Departments</CardTitle>
            <Button size="sm" onClick={() => setShowDeptModal(true)}>
              <Plus className="h-4 w-4" />
              Add Department
            </Button>
          </CardHeader>
          <div className="space-y-2">
            {departments.map((dept) => (
              <div
                key={dept.id}
                className="flex items-center justify-between py-3 px-4 rounded-lg border border-border/50 hover:border-border transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    {dept.name}
                  </p>
                  {dept.code && (
                    <p className="text-xs text-text-muted">Code: {dept.code}</p>
                  )}
                </div>
                <Badge variant={dept.is_active ? "success" : "default"}>
                  {dept.is_active ? "Active" : "Archived"}
                </Badge>
              </div>
            ))}
            {departments.length === 0 && (
              <p className="text-sm text-text-muted text-center py-8">
                No departments created yet
              </p>
            )}
          </div>

          <Modal
            isOpen={showDeptModal}
            onClose={() => setShowDeptModal(false)}
            title="Add Department"
          >
            <div className="space-y-4">
              <Input
                label="Department Name"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                placeholder="e.g., Engineering"
                autoFocus
              />
              <Input
                label="Code (optional)"
                value={newDeptCode}
                onChange={(e) => setNewDeptCode(e.target.value)}
                placeholder="e.g., ENG"
              />
              <div className="flex justify-end gap-3">
                <Button variant="secondary" onClick={() => setShowDeptModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateDept}>Create</Button>
              </div>
            </div>
          </Modal>
        </Card>
      )}

      {activeTab === "leave" && <LeavePolicySettings />}

      {activeTab === "time" && <ProjectsSettings />}

      {activeTab === "templates" && <DocumentTemplatesSettings />}

      {activeTab === "doc-config" && <DocumentConfigSettings />}

      {activeTab === "email-templates" && <EmailTemplatesSettings />}

      {activeTab === "email-log" && <EmailLogSettings />}

      {activeTab === "compliance" && <ComplianceSettings />}

      {activeTab === "billing" && <BillingSettings />}

      {activeTab === "security" && (
        <div className="space-y-6">
          {/* SUPER_ADMINs see the org-wide policy + sessions + login
              activity at the top, then their own self-service section
              below. Non-admins just see self-service. */}
          {isSuperAdmin && <OrgSecurityPolicySettings />}
          <SecuritySettings />
        </div>
      )}

      {activeTab === "sso" && <SSOSettings />}

      {activeTab === "privacy" && <PrivacyRequestsSettings />}

      {activeTab === "integrations" && <IntegrationsSettings />}
      {activeTab === "custom-fields" && <CustomFieldsSettings />}
      {activeTab === "workflows" && <WorkflowsSettings />}

      {activeTab === "audit" && isSuperAdmin && <AuditLogSettings />}
      {activeTab === "users" && isSuperAdmin && <UsersSettings />}

      {activeTab === "exports" && isSuperAdmin && <ExportsSettings />}

      {activeTab === "payroll" && isSuperAdmin && <PayrollIntegrationSettings />}
    </motion.div>
  );
}
